import React, { useState } from 'react';
import styled from 'styled-components';
import { UnmountClosed } from 'react-collapse';
import Header from './Header';
import Link from '../../../../../../../shared/components/Link';
import { formatMoney } from '~/helpers/currency';
import { useTranslation } from '~/Locale';
import { CarServiceIcon, NextIcon } from '@kvdbil/components';
import { AlignIcon } from '../../../../../../../shared/components/Layout/AlignIcon';
import { OptionType } from '~/App/shared/types/OptionType';
import { CMSDataObjectPageService } from '~/App/views/ObjectPage/cmsDataHandlers';
import { ServiceStructuredText } from './ServiceStructuredText';
import { ServiceContainer } from '..';

const CostWrapper = styled.div`
  padding: 2.5rem 0 2rem;
`;

const LinkWrapper = styled.div`
  text-align: center;
  font-weight: 500;
  padding-bottom: 2rem;
`;

const CostText = styled.div`
  display: flex;
  flex-direction: row;
`;

const CostLabel = styled.div`
  flex: 1;
  font-size: 1em;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const CostValue = styled.div`
  flex: 1;
  font-size: 1em;
  color: ${({ theme }) => theme.colors.text.dark};
  text-align: right;
  b {
    font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
  }
`;

interface Props {
  cmsData?: CMSDataObjectPageService;
  releasingInformation?: {
    selectedDistance?: OptionType;
    selectedPeriod?: OptionType;
  };
}

const ServiceAgreementReleasing = ({
  cmsData,
  releasingInformation = {}
}: Props) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = () => setExpanded(isExpanded => !isExpanded);
  const distance = releasingInformation?.selectedDistance?.label;
  const period = releasingInformation?.selectedPeriod?.label;
  let calculatedMonthlyCost = 0;
  let description = '';

  const monthlyCost = cmsData?.jsonData;
  if (distance && period && monthlyCost) {
    calculatedMonthlyCost = monthlyCost[distance][period];
    description = t(
      'From %1$s kr/month',
      calculatedMonthlyCost ? calculatedMonthlyCost : monthlyCost?.[500]?.[12]
    );
  }
  const ariaControls = 'additional-service-agreement-releasing-content';
  return (
    <ServiceContainer>
      <Header
        icon={cmsData?.icon ?? CarServiceIcon}
        isExpanded={expanded}
        toggleExpanded={toggleExpanded}
        responsiveImage={cmsData?.image?.responsiveImage}
        title={cmsData?.title ?? ''}
        description={description}
        ariaControls={ariaControls}
      />
      <UnmountClosed isOpened={expanded}>
        <div id={ariaControls}>
          <ServiceStructuredText data={cmsData?.content} />
          <CostWrapper>
            <CostText>
              <CostLabel>{t('Mileage per year')}</CostLabel>
              <CostValue>
                {distance} {t('unit.scandinavian_miles')}
              </CostValue>
            </CostText>
            <CostText>
              <CostLabel>{t('Leasing period')}</CostLabel>
              <CostValue>
                {period} {t('months')}
              </CostValue>
            </CostText>
            <CostText>
              <CostLabel>{t('Service agreement')}</CostLabel>
              <CostValue>
                <b>
                  {formatMoney({
                    value: calculatedMonthlyCost,
                    currency: 'kr'
                  })}
                  {t('/month')}
                </b>
              </CostValue>
            </CostText>
          </CostWrapper>
          <LinkWrapper>
            <Link to={cmsData?.link?.[0]?.url} target="_blank">
              <AlignIcon left={0.15}>
                {cmsData?.link?.[0]?.text}
                <NextIcon />
              </AlignIcon>
            </Link>
          </LinkWrapper>
        </div>
      </UnmountClosed>
    </ServiceContainer>
  );
};

export default ServiceAgreementReleasing;
